/**
 * "Exportera" i Affärer–Pipeline: Excel för människor.
 *
 * Skriver flikarna från modules/crm/vanligExport.js (Leads, Kunder,
 * Kontakter, Om filen) med
 *   - bilder i första kolumnen, 32 × 32 px, förankrade i cellen
 *     ("flytta och ändra storlek med celler") så att de följer raden vid
 *     sortering och filtrering
 *   - id-kolumnerna längst till höger, dolda i en ihopfälld kolumngrupp (+)
 *   - fryst rubrikrad, autofilter, kolumnbredd efter innehåll, datum som
 *     riktiga datum och radbrytning med maxbredd i långa textfält
 *
 * byggVanligArbetsbok är ren (ExcelJS skickas in) och går att testa i Node.
 * ExcelJS laddas dynamiskt så att biblioteket hamnar i en egen chunk.
 */
import { hamtaBilder } from './excelBilder';

const ORANGE = 'FFFF6B00';
const DARK   = 'FF1A1A1A';

const MAX_BREDD = 45;
const MAX_BREDD_WRAP = 60;
const MIN_BREDD = 8;
const BILD_BREDD = 6;      // ≈ 47 px
const BILD_RADHOJD = 30;   // pt ≈ 40 px
const BILD_PX = 32;
const EMU_PER_PX = 9525;

/** '#E8F5E9' → 'FFE8F5E9' (ExcelJS vill ha ARGB). */
function argb(hex, fallback) {
  const h = String(hex || '').replace('#', '');
  return h.length === 6 ? `FF${h.toUpperCase()}` : fallback;
}

/** Ungefärlig visningsbredd i tecken, för kolumnbredden. */
function visningslangd(v, col) {
  if (v == null || v === '') return 0;
  if (v instanceof Date) return 10;
  if (typeof v === 'number') {
    if (col.format === '0 %') return 5;
    return Math.round(Math.abs(v)).toLocaleString('sv-SE').length + 1;
  }
  return Math.max(...String(v).split('\n').map(rad => rad.length));
}

function kolumnBredd(col, rader) {
  if (col.bredd) return col.bredd;
  if (col.bild) return BILD_BREDD;
  const langst = Math.max(String(col.header || '').length, ...rader.map(r => visningslangd(r[col.key], col)));
  return Math.min(Math.max(langst + 2, MIN_BREDD), col.wrap ? MAX_BREDD_WRAP : MAX_BREDD);
}

/** Radhöjd i pt för radbrutna fält: antal rader de tar, högst 6. */
function uppskattadHojd(rad, wrapKolumner, bredder) {
  let rader = 1;
  for (const col of wrapKolumner) {
    const v = rad[col.key];
    if (!v) continue;
    const n = String(v).split('\n').reduce((s, del) => s + Math.max(1, Math.ceil(del.length / bredder[col.key])), 0);
    rader = Math.max(rader, n);
  }
  return rader > 1 ? Math.min(rader, 6) * 15 : undefined;
}

/** Ankare som håller bilden inom cellen, centrerad. Kolumnbredd i tecken, radhöjd i pt. */
function bildAnkare(kolIdx, radIdx, bredd, hojdPt) {
  const cellB = Math.round(bredd * 7 + 5);
  const cellH = Math.round(hojdPt * 96 / 72);
  const x = Math.max(0, Math.floor((cellB - BILD_PX) / 2));
  const y = Math.max(0, Math.floor((cellH - BILD_PX) / 2));
  return {
    tl: { nativeCol: kolIdx, nativeColOff: x * EMU_PER_PX, nativeRow: radIdx, nativeRowOff: y * EMU_PER_PX },
    br: { nativeCol: kolIdx, nativeColOff: (x + BILD_PX) * EMU_PER_PX, nativeRow: radIdx, nativeRowOff: (y + BILD_PX) * EMU_PER_PX },
    editAs: 'twoCell',
  };
}

function skrivFlik(wb, b, bildId) {
  // Id-kolumnerna sist, i flikens ordning.
  const kolumner = [...b.kolumner.filter(c => !c.id), ...b.kolumner.filter(c => c.id)];
  const harBild = kolumner.some(c => c.bild);
  const bredder = Object.fromEntries(kolumner.map(c => [c.key, kolumnBredd(c, b.rader)]));
  const wrapKolumner = kolumner.filter(c => c.wrap);

  const ws = wb.addWorksheet(b.namn, {
    views: [{ state: 'frozen', xSplit: b.frysKolumner || 0, ySplit: 1 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  ws.columns = kolumner.map(c => ({ key: c.key, width: bredder[c.key] }));

  // ── Rubrikrad ──
  const head = ws.getRow(1);
  kolumner.forEach((c, i) => {
    const cell = head.getCell(i + 1);
    cell.value = c.header;
    cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = { bottom: { style: 'thin', color: { argb: ORANGE } } };
  });
  head.height = 22;

  // ── Datarader ──
  b.rader.forEach((rad, idx) => {
    const radNr = idx + 2;
    const row = ws.getRow(radNr);
    const hojd = harBild ? BILD_RADHOJD : uppskattadHojd(rad, wrapKolumner, bredder);
    if (hojd) row.height = hojd;

    kolumner.forEach((c, i) => {
      const cell = row.getCell(i + 1);
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFE0E0E0' } } };
      if (c.bild) {
        const id = rad._bild ? bildId(rad._bild) : null;
        if (id != null) ws.addImage(id, bildAnkare(i, radNr - 1, bredder[c.key], BILD_RADHOJD));
        return;
      }
      const v = rad[c.key];
      if (v !== '' && v != null) cell.value = v;
      cell.font = { size: 10, bold: !!c.fet, color: c.id ? { argb: 'FF8A8A8A' } : undefined };
      cell.alignment = {
        vertical: c.wrap ? 'top' : 'middle',
        wrapText: !!c.wrap,
        horizontal: c.format && c.format !== 'yyyy-mm-dd' ? 'right' : 'left',
      };
      if (c.format) cell.numFmt = c.format;
      else if (v instanceof Date) cell.numFmt = 'yyyy-mm-dd';

      if (c.lank && v) {
        const href = /^https?:\/\//i.test(v) ? v : `https://${v}`;
        cell.value = { text: String(v), hyperlink: href };
        cell.font = { size: 10, color: { argb: 'FF1565C0' }, underline: true };
      }
      if (c.stil === 'status') {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(rad._statusBg, 'FFF5F5F5') } };
        cell.font = { size: 10, bold: true, color: { argb: argb(rad._statusColor, DARK) } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      }
      if (c.stil === 'forfallen' && rad._followUpOverdue) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEE' } };
        cell.font = { size: 10, bold: true, color: { argb: 'FFC62828' } };
      }
    });
  });

  // ── Summarad, med en tom rad emellan så att sortering inte tar med den ──
  if (b.summa && b.rader.length) {
    const sista = b.rader.length + 1;
    const row = ws.getRow(sista + 2);
    row.height = 22;
    kolumner.forEach((c, i) => {
      const cell = row.getCell(i + 1);
      cell.border = { top: { style: 'medium', color: { argb: DARK } } };
      if (c.key === b.summa.etikett) {
        cell.value = `Totalt (${b.rader.length})`;
        cell.font = { bold: true, size: 10 };
      } else if (b.summa.keys.includes(c.key)) {
        const bokstav = ws.getColumn(i + 1).letter;
        cell.value = { formula: `SUM(${bokstav}2:${bokstav}${sista})` };
        cell.numFmt = c.format;
        cell.font = { bold: true, size: 10, color: { argb: ORANGE } };
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });
  }

  // ── Id-kolumnerna: dolda i en ihopfälld grupp ──
  if (kolumner.some(c => c.id)) {
    ws.properties.outlineLevelCol = 1;
    kolumner.forEach((c, i) => {
      if (!c.id) return;
      const col = ws.getColumn(i + 1);
      col.outlineLevel = 1;
      col.hidden = true;
    });
  }

  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: kolumner.length } };
  return ws;
}

/**
 * @param {object}   ExcelJS  biblioteket (default-exporten)
 * @param {Array}    blad     från byggVanligaBlad
 * @param {Map}      bilder   källa → { base64, extension } (från hamtaBilder)
 */
export function byggVanligArbetsbok(ExcelJS, blad, bilder = new Map(), { exportedAt = new Date() } = {}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'LagbasN';
  wb.created = exportedAt;

  // Varje bild läggs in en gång i arbetsboken, hur många rader som än använder den.
  const ids = new Map();
  const bildId = kalla => {
    if (!bilder.has(kalla)) return null;
    if (!ids.has(kalla)) ids.set(kalla, wb.addImage(bilder.get(kalla)));
    return ids.get(kalla);
  };

  for (const b of blad) skrivFlik(wb, b, bildId);
  return wb;
}

/** Laddar ner arbetsboken som .xlsx. */
export async function laddaNerArbetsbok(wb, fileName) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Hämtar bilderna, bygger arbetsboken och laddar ner den. */
export async function exportVanligExcel(blad, { fileName = 'affarer-pipeline.xlsx', exportedAt, onProgress } = {}) {
  const ExcelJS = (await import('exceljs')).default;
  const kallor = blad.flatMap(b => (b.kolumner.some(c => c.bild) ? b.rader.map(r => r._bild) : []));
  const bilder = await hamtaBilder(kallor, {
    onProgress: (klara, totalt) => onProgress?.(`Hämtar bilder ${klara}/${totalt}…`),
  });
  onProgress?.('Skriver Excel…');
  await laddaNerArbetsbok(byggVanligArbetsbok(ExcelJS, blad, bilder, { exportedAt }), fileName);
}
